import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, Button as MuiButton } from "@mui/material";
import ReactSelect from "react-select";
import moment from "moment";
import "moment-timezone";

import { useSelector } from "react-redux";
import { useRealtimeUnits } from 'Core/hooks';
import { useClipRequestDrawer } from "../useClipRequestDrawer";
import { useIntl } from "react-intl";
import { PrivateClipRequest } from "Modules/.common/components/PrivateClipRequest";

const LIGHTMETRICS_MANUFACTURER = "LIGHTMETRICS";

/**
 * Busca la unidad en el catálogo por deviceId: por id de unidad o por id del primer videoDevice.
 * @param {Array} units - Lista de unidades del catálogo
 * @param {string|number} deviceId - Id del dispositivo a buscar
 * @returns {object|undefined} Unidad encontrada o undefined
 */
const findUnitByDeviceId = (units, deviceId) => {
  if (!units?.length || deviceId == null) return undefined;
  const id = String(deviceId);
  return units.find(
    (unit) =>
      String(unit?.id) === id || String(unit?.videoDevices?.[0]?.id) === id,
  );
};

/**
 * Obtiene el nombre del fabricante de la unidad (linkedUnit o primer videoDevice).
 * @param {object} unit - Unidad del catálogo
 * @returns {string|undefined}
 */
const getUnitManufacturer = (unit) =>
  unit?.linkedUnit?.manufacturerName ?? unit?.videoDevices?.[0]?.manufacturer;

/** Estructura de opciones; labels se traducen con useClipRequestOptions. */
const RESOLUTION_OPTION_IDS = [
  {
    id: "640x360",
    labelKey: "realtimemaps_vehicletrails_cliprequest_resolution_sd",
  },
  {
    id: "1280x720",
    labelKey: "realtimemaps_vehicletrails_cliprequest_resolution_hd",
  },
];
const STYLE_OPTION_IDS = [
  { id: "road", labelKey: "realtimemaps_vehicletrails_cliprequest_style_road" },
  {
    id: "sideBySide",
    labelKey: "realtimemaps_vehicletrails_cliprequest_style_sideBySide",
  },
  {
    id: "pictureInPicture",
    labelKey: "realtimemaps_vehicletrails_cliprequest_style_pictureInPicture",
  },
  {
    id: "driver",
    labelKey: "realtimemaps_vehicletrails_cliprequest_style_driver",
  },
  {
    id: "separate",
    labelKey: "realtimemaps_vehicletrails_cliprequest_style_separate",
  },
];
const CAMERA_VIEW_OPTION_IDS = [
  {
    id: "road",
    labelKey: "realtimemaps_vehicletrails_cliprequest_cameraView_road",
  },
  {
    id: "driver",
    labelKey: "realtimemaps_vehicletrails_cliprequest_cameraView_driver",
  },
];

const buildOptionsWithLabels = (optionIds, messages) =>
  optionIds.map(({ id, labelKey }) => ({
    id,
    label: messages[labelKey] ?? id,
  }));

/** Estilos para el menú en portal y control: evita clipping/z-index en AreaMenu/Tooltip. */
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    minHeight: 38,
    border: "1px solid #e0e0e0",
    "&:hover": { borderColor: "#bdbdbd" },
  }),
};

/**
 * Select que renderiza el menú en document.body para funcionar correctamente
 * dentro de AreaMenu (Tooltip + ClickAwayListener) y contenedores con overflow.
 */
const VisualSelect = ({ label, options, value, onChange, placeholder }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <Typography
      component="label"
      sx={{ fontSize: "13px", fontWeight: 500, color: "#666" }}
    >
      {label}:
    </Typography>
    <ReactSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      getOptionLabel={(opt) => opt.label}
      getOptionValue={(opt) => opt.id}
      menuPortalTarget={document.body}
      styles={selectStyles}
      isClearable={false}
      classNamePrefix="visual-select"
    />
  </Box>
);

const MAX_RANGE_MINUTES = 5;
const MIN_DURATION_SECONDS = 60;

const parseInitialDate = (dateProp) => {
  if (dateProp == null || dateProp === "") return null;
  const ts =
    typeof dateProp === "number" && dateProp < 10000000000
      ? dateProp * 1000
      : dateProp;
  const parsed = moment(ts);
  return parsed.isValid() ? parsed.toDate() : null;
};

const formatDuration = (from, to) => {
  if (!from || !to) return "0min, 0sec";
  const diffMs = new Date(to) - new Date(from);
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}min, ${secs}sec`;
};

const clampDate = (val, minDate, maxDate) => {
  const t = new Date(val).getTime();
  const min =
    minDate != null ? new Date(minDate).getTime() : Number.NEGATIVE_INFINITY;
  const max =
    maxDate != null ? new Date(maxDate).getTime() : Number.POSITIVE_INFINITY;
  if (t < min) return new Date(min);
  if (t > max) return new Date(max);
  return new Date(val);
};

const TimeSegmentInput = ({ value, onChange, label, minDate, maxDate, timezone }) => {
  const toTz = (v) => timezone ? moment(v).tz(timezone) : moment(v);
  const m = toTz(value);
  const [minutes, setMinutes] = useState(m.minutes());
  const [seconds, setSeconds] = useState(m.seconds());

  const applyChange = useCallback(
    (newMinutes, newSeconds) => {
      const mtz = timezone ? moment(value).tz(timezone) : moment(value);
      mtz.minutes(newMinutes);
      mtz.seconds(newSeconds);
      const clamped = clampDate(mtz.toDate(), minDate, maxDate);
      onChange(clamped);
    },
    [value, onChange, minDate, maxDate, timezone],
  );

  useEffect(() => {
    const mtz = timezone ? moment(value).tz(timezone) : moment(value);
    setMinutes(mtz.minutes());
    setSeconds(mtz.seconds());
  }, [value, timezone]);

  const handleKeyDown = (e, segment) => {
    const isMinutes = segment === "minutes";
    const current = isMinutes ? minutes : seconds;
    const max = 59;
    const min = 0;
    let next = current;

    if (e.key === "ArrowUp") {
      e.preventDefault();
      next = current >= max ? min : current + 1;
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      next = current <= min ? max : current - 1;
    } else if (e.key >= "0" && e.key <= "9") {
      return;
    }

    if (next !== current) {
      const newVal = Math.min(max, Math.max(min, next));
      if (isMinutes) {
        setMinutes(newVal);
        applyChange(newVal, seconds);
      } else {
        setSeconds(newVal);
        applyChange(minutes, newVal);
      }
    }
  };

  const handleMinutesChange = (e) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v)) {
      const clamped = Math.min(59, Math.max(0, v));
      setMinutes(clamped);
      applyChange(clamped, seconds);
    }
  };

  const handleSecondsChange = (e) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v)) {
      const clamped = Math.min(59, Math.max(0, v));
      setSeconds(clamped);
      applyChange(minutes, clamped);
    }
  };

  const mDisplay = toTz(value);
  const datePart = mDisplay.format("MM/DD/YYYY hh:");
  const ampm = mDisplay.format(" a");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Typography
        component="label"
        sx={{ fontSize: "13px", fontWeight: 500, color: "#666" }}
      >
        {label}:
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          flexWrap: "wrap",
          backgroundColor: "#fff",
          borderRadius: "4px",
          padding: "8px 12px",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: "14px", color: "#666", mr: 1 }}
        >
          🕐
        </Typography>
        <Typography component="span" sx={{ fontSize: "14px", color: "#333" }}>
          {datePart}
        </Typography>
        <input
          type="number"
          min={0}
          max={59}
          value={String(minutes).padStart(2, "0")}
          onChange={handleMinutesChange}
          onKeyDown={(e) => handleKeyDown(e, "minutes")}
          aria-label={`${label} minutos`}
          style={{
            width: 28,
            fontSize: 14,
            textAlign: "center",
            border: "1px solid #ddd",
            borderRadius: 4,
            padding: "4px 2px",
          }}
        />
        <Typography component="span" sx={{ fontSize: "14px", color: "#333" }}>
          :
        </Typography>
        <input
          type="number"
          min={0}
          max={59}
          value={String(seconds).padStart(2, "0")}
          onChange={handleSecondsChange}
          onKeyDown={(e) => handleKeyDown(e, "seconds")}
          aria-label={`${label} segundos`}
          style={{
            width: 28,
            fontSize: 14,
            textAlign: "center",
            border: "1px solid #ddd",
            borderRadius: 4,
            padding: "4px 2px",
          }}
        />
        <Typography component="span" sx={{ fontSize: "14px", color: "#333" }}>
          {ampm}
        </Typography>
      </Box>
    </Box>
  );
};

export const DrawerRequestForm = ({ deviceId, initialDate, timezone }) => {
  const { messages } = useIntl();
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [resolution, setResolution] = useState({ id: "1280x720", label: "HD" });
  const [style, setStyle] = useState({ id: "road", label: "road" });
  const [cameraView, setCameraView] = useState({ id: "road", label: "road" });
  const [privateClipChecked, setPrivateClipChecked] = useState(false);

  const resolutionOptions = useMemo(
    () => buildOptionsWithLabels(RESOLUTION_OPTION_IDS, messages),
    [messages],
  );
  const styleOptions = useMemo(
    () => buildOptionsWithLabels(STYLE_OPTION_IDS, messages),
    [messages],
  );
  const cameraViewOptions = useMemo(
    () => buildOptionsWithLabels(CAMERA_VIEW_OPTION_IDS, messages),
    [messages],
  );
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const { createClipRequest, loadingCreate } = useClipRequestDrawer();

  const unitFromCatalog = useMemo(
    () => findUnitByDeviceId(units, deviceId),
    [units, deviceId],
  );
  const manufacturer = useMemo(
    () => getUnitManufacturer(unitFromCatalog),
    [unitFromCatalog],
  );
  const isLightMetrics = useMemo(
    () => manufacturer?.toUpperCase() === LIGHTMETRICS_MANUFACTURER,
    [manufacturer],
  );

  useEffect(() => {
    const parsed = parseInitialDate(initialDate);
    if (parsed) {
      const fromDate = new Date(parsed);
      const toDate = new Date(parsed.getTime() + MAX_RANGE_MINUTES * 60 * 1000);
      setFrom(fromDate);
      setTo(toDate);
    }
    setPrivateClipChecked(false);
  }, [initialDate]);

  const handleFromChange = useCallback((newFrom) => {
    setFrom(newFrom);
    setTo((prev) => {
      if (!prev) return prev;
      const minTo = new Date(newFrom.getTime() + MIN_DURATION_SECONDS * 1000);
      const maxTo = new Date(newFrom.getTime() + MAX_RANGE_MINUTES * 60 * 1000);
      const t = prev.getTime();
      if (t < minTo.getTime()) return minTo;
      if (t > maxTo.getTime()) return maxTo;
      return prev;
    });
  }, []);

  const handleToChange = useCallback((newTo) => {
    setTo(newTo);
    setFrom((prev) => {
      if (!prev) return prev;
      const maxFrom = new Date(newTo.getTime() - MIN_DURATION_SECONDS * 1000);
      return prev.getTime() > maxFrom.getTime() ? maxFrom : prev;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const start = moment(from).utc().format();
    const end = moment(to).utc().format();
    createClipRequest({
      deviceId,
      dateRange: {
        start,
        end,
      },
      isPrivate: privateClipChecked,
    });
  }, [from, to, deviceId, privateClipChecked, createClipRequest]);

  if (!from || !to) {
    return null;
  }

  const minTo = new Date(from.getTime() + MIN_DURATION_SECONDS * 1000);
  const maxFrom = new Date(to.getTime() - MIN_DURATION_SECONDS * 1000);
  const maxTo = new Date(from.getTime() + MAX_RANGE_MINUTES * 60 * 1000);

  return (
    <Box
      sx={{
        padding: "16px",
        backgroundColor: "#f9f9f9",
        borderRadius: "4px",
      }}
    >
      {isLightMetrics && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <VisualSelect
            label={
              messages["realtimemaps_vehicletrails_cliprequest_resolution"]
            }
            options={resolutionOptions}
            value={resolution}
            onChange={(opt) => setResolution(opt || resolutionOptions[1])}
            placeholder={
              messages[
                "realtimemaps_vehicletrails_cliprequest_placeholder_resolution"
              ]
            }
          />
          <VisualSelect
            label={messages["realtimemaps_vehicletrails_cliprequest_style"]}
            options={styleOptions}
            value={style}
            onChange={(opt) => setStyle(opt || styleOptions[0])}
            placeholder={
              messages[
                "realtimemaps_vehicletrails_cliprequest_placeholder_style"
              ]
            }
          />
          <VisualSelect
            label={
              messages["realtimemaps_vehicletrails_cliprequest_cameraView"]
            }
            options={cameraViewOptions}
            value={cameraView}
            onChange={(opt) => setCameraView(opt || cameraViewOptions[0])}
            placeholder={
              messages[
                "realtimemaps_vehicletrails_cliprequest_placeholder_cameraView"
              ]
            }
          />
        </Box>
      )}

      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          marginBottom: "16px",
          color: "#333",
        }}
      >
        {messages["realtimemaps_vehicletrails_requestvideo"]}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <TimeSegmentInput
          value={from}
          onChange={handleFromChange}
          label={messages["realtimemaps_vehicletrails_from"]}
          minDate={null}
          maxDate={maxFrom}
          timezone={timezone}
        />
        <TimeSegmentInput
          value={to}
          onChange={handleToChange}
          label={messages["realtimemaps_vehicletrails_to"]}
          minDate={minTo}
          maxDate={maxTo}
          timezone={timezone}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#666" }}>
            {messages["realtimemaps_vehicletrails_duration"]}:
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#333" }}>
            {formatDuration(from, to)}
          </Typography>
        </Box>
        <PrivateClipRequest
          checked={privateClipChecked}
          setChecked={setPrivateClipChecked}
        />
        <MuiButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loadingCreate}
          sx={{
            marginTop: "8px",
            padding: "10px 20px",
            backgroundColor: "#005bf8",
            fontSize: "14px",
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#014dd1",
            },
            "&:active": {
              backgroundColor: "#033997",
            },
          }}
        >
          {messages["realtimemaps_vehicletrails_submit"]}
        </MuiButton>
      </Box>
    </Box>
  );
};
